import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";

/**
 * Modal — Ocean Blue Design System
 *
 * Props:
 *  isOpen     : boolean
 *  onClose    : () => void
 *  title      : string | ReactNode
 *  subtitle   : string
 *  size       : "sm" | "md" | "lg" | "xl" | "full"
 *  footer     : ReactNode — custom footer; if omitted no footer rendered
 *  closable   : boolean — show × button (default true)
 *  closeOnOverlay : boolean (default true)
 *  danger     : boolean — red header accent for destructive actions
 */

const SIZES = {
  sm:   "max-w-sm",
  md:   "max-w-md",
  lg:   "max-w-2xl",
  xl:   "max-w-4xl",
  "2xl":"max-w-6xl",
  full: "max-w-[95vw]",
};

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  closable = true,
  closeOnOverlay = true,
  danger = false,
  className = "",
}) => {
  const overlayRef = useRef(null);

  /* Lock body scroll when open */
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen || !closable) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, closable, onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (closeOnOverlay && closable && e.target === overlayRef.current) {
        onClose?.();
      }
    },
    [closeOnOverlay, closable, onClose]
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#0F172A]/50 backdrop-blur-[2px] animate-fade-in"
      aria-modal="true"
      role="dialog"
      aria-label={typeof title === "string" ? title : "Dialog"}
    >
      {/* Panel */}
      <div
        className={[
          "relative w-full bg-white rounded-2xl",
          "shadow-[0_20px_60px_rgba(15,23,42,0.20),0_8px_16px_rgba(15,23,42,0.10)]",
          "flex flex-col max-h-[90vh] animate-slide-in",
          SIZES[size] ?? SIZES.md,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Header */}
        {(title || closable) && (
          <div
            className={[
              "flex items-start justify-between px-6 py-4 rounded-t-2xl flex-shrink-0",
              danger
                ? "border-b border-[#FECACA] bg-[#FEF2F2]"
                : "border-b border-[#F1F5F9] bg-white",
            ].join(" ")}
          >
            <div className="min-w-0 pr-4">
              {title && (
                <h2
                  className={[
                    "text-base font-semibold leading-tight",
                    danger ? "text-[#991B1B]" : "text-[#0F172A]",
                  ].join(" ")}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm text-[#94A3B8] mt-1 leading-snug">{subtitle}</p>
              )}
            </div>
            {closable && (
              <button
                onClick={onClose}
                aria-label="Close dialog"
                className={[
                  "flex-shrink-0 p-1.5 rounded-lg transition-colors duration-150",
                  "text-[#94A3B8] hover:text-[#1E293B] hover:bg-[#F1F5F9]",
                  "focus:outline-none focus:ring-2 focus:ring-[#004BB5]/30",
                ].join(" ")}
              >
                {/* × icon */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M2 2l12 12M14 2L2 14" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#F1F5F9] flex-shrink-0 rounded-b-2xl bg-[#F8FAFC]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/**
 * ConfirmModal — pre-built confirm/cancel pattern
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  isLoading = false,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size="sm"
    danger={danger}
    footer={
      <>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={isLoading}>
          {cancelLabel}
        </Button>
        <Button
          variant={danger ? "danger" : "primary"}
          size="sm"
          onClick={onConfirm}
          isLoading={isLoading}
        >
          {confirmLabel}
        </Button>
      </>
    }
  >
    {message && <p className="text-sm text-[#475569] leading-relaxed">{message}</p>}
  </Modal>
);

export default Modal;
